import { useEffect } from 'react'
import { useConvexAuth, useQuery } from 'convex/react'
import type { ReactNode } from 'react'
import { api } from '@backend/_generated/api'
import { FullPageSpinner } from '@/components/common/full-page-spinner'
import { useAuthStore } from '@/stores/authStore'

/**
 * Auth Sync Provider — syncs Convex Auth state to Zustand.
 *
 * Reads `isAuthenticated` and `isLoading` from Convex Auth and propagates
 * them to the Zustand auth store so the rest of the app can read auth state
 * without depending on Convex hooks directly.
 *
 * Must be nested inside ConvexAuthProvider and QueryProvider:
 *
 * @example
 * ```tsx
 * <ConvexAuthProvider client={convex}>
 *   <QueryProvider>
 *     <AuthSyncProvider>
 *       <App />
 *     </AuthSyncProvider>
 *   </QueryProvider>
 * </ConvexAuthProvider>
 * ```
 */
export function AuthSyncProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, isLoading } = useConvexAuth()

  // Fetch profile and organizations only when authenticated
  const profile = useQuery(
    api.profile.getMyProfile,
    isAuthenticated ? {} : 'skip',
  )
  const organizations = useQuery(
    api.profile.getMyOrganizations,
    isAuthenticated ? {} : 'skip',
  )

  const isLoggingOut = useAuthStore((s) => s.isLoggingOut)
  const setIsAuthenticated = useAuthStore((s) => s.setIsAuthenticated)
  const setIsLoading = useAuthStore((s) => s.setIsLoading)
  const setUser = useAuthStore((s) => s.setUser)
  const setCurrentOrganization = useAuthStore((s) => s.setCurrentOrganization)
  const currentOrganization = useAuthStore((s) => s.currentOrganization)
  const reset = useAuthStore((s) => s.reset)

  useEffect(() => {
    setIsLoading(isLoading)
    setIsAuthenticated(isAuthenticated)

    if (!isAuthenticated && !isLoading) {
      reset()
    }
  }, [isAuthenticated, isLoading, setIsAuthenticated, setIsLoading, reset])

  // Sync user profile to auth store
  useEffect(() => {
    if (isAuthenticated && profile) {
      setUser({
        userId: profile.userId,
        name: profile.name,
        email: profile.email,
        image: profile.image,
      })
    }
  }, [isAuthenticated, profile, setUser])

  // Set first organization as current org on initial load only.
  // Does NOT overwrite a manually switched org — reset() sets currentOrganization
  // back to null on logout, allowing the first org to be selected again on next login.
  useEffect(() => {
    if (
      isAuthenticated &&
      organizations &&
      organizations.length > 0 &&
      !currentOrganization
    ) {
      const first = organizations[0]
      setCurrentOrganization({
        organizationId: first.organizationId,
        isAdmin: first.isAdmin,
        roles: first.roles,
      })
    }
  }, [
    isAuthenticated,
    organizations,
    setCurrentOrganization,
    currentOrganization,
  ])

  if (isLoggingOut) {
    return <FullPageSpinner message="Logging out..." />
  }

  if (isLoading) {
    return <FullPageSpinner message="Initializing..." />
  }

  return <>{children}</>
}
